import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import {
  AlertTriangle,
  BarChart3,
  Bug,
  Clock3,
  Hammer,
  ShieldCheck,
} from 'lucide-react'
import { workloadApi } from '../api/workload'
import { Avatar } from '../components/common/Avatar'
import { formatRoleLabel } from '../lib/roles'
import { formatWorkType } from '../lib/workTypes'
import type { PerformanceEmployeeMetric, PerformanceTrendPoint, WorkType } from '../types'

const todayDateInput = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

const addDaysToInput = (value: string, days: number) => {
  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  date.setDate(date.getDate() + days)
  const nextMonth = String(date.getMonth() + 1).padStart(2, '0')
  const nextDay = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${nextMonth}-${nextDay}`
}

const formatHours = (hours: number) => {
  if (!Number.isFinite(hours) || hours <= 0) return '0h'
  const wholeHours = Math.floor(hours)
  const minutes = Math.round((hours - wholeHours) * 60)
  if (minutes === 60) return `${wholeHours + 1}h`
  return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`
}

const formatPercent = (value: number) => `${value.toFixed(1)}%`

const statusTone: Record<PerformanceEmployeeMetric['status'], { bg: string; color: string; label: string }> = {
  GOOD: { bg: '#ecfdf3', color: '#166534', label: 'Healthy' },
  WATCH: { bg: '#fffbeb', color: '#92400e', label: 'Watch' },
  RISK: { bg: '#fef2f2', color: '#991b1b', label: 'Risk' },
  LOW_DATA: { bg: '#eff6ff', color: '#1d4ed8', label: 'Low data' },
}

const workTypeColors: Record<WorkType, string> = {
  UI_DESIGN: '#0f766e',
  FRONTEND_DEVELOPMENT: '#2563eb',
  BACKEND_DEVELOPMENT: '#7c3aed',
  TESTING: '#16a34a',
  FRONTEND_BUG_FIX: '#f97316',
  BACKEND_BUG_FIX: '#ef4444',
  REVIEW: '#475569',
  MEETING: '#475569',
  SUPPORT: '#475569',
  OTHER: '#8b5cf6',
}

const chartWorkTypes: WorkType[] = [
  'UI_DESIGN',
  'FRONTEND_DEVELOPMENT',
  'BACKEND_DEVELOPMENT',
  'FRONTEND_BUG_FIX',
  'BACKEND_BUG_FIX',
  'TESTING',
  'MEETING',
  'OTHER',
]

export default function PeoplePerformancePage() {
  const today = todayDateInput()
  const [startDate, setStartDate] = useState(addDaysToInput(today, -6))
  const [endDate, setEndDate] = useState(today)
  const [employeeId, setEmployeeId] = useState('all')

  const { data, isLoading } = useQuery({
    queryKey: ['people-performance', startDate, endDate, employeeId],
    queryFn: () => workloadApi.getPerformance({ startDate, endDate, employeeId }),
  })

  const review = data?.data?.data
  const totals = review?.totals
  const metrics = review?.employeeMetrics || []
  const trend = review?.trend || []

  const busiestEmployees = useMemo(
    () => [...metrics].sort((a, b) => b.totalHours - a.totalHours).slice(0, 4),
    [metrics]
  )

  return (
    <div style={pageStyle}>
      <div style={heroStyle}>
        <div>
          <div style={eyebrowStyle}>People Performance</div>
          <h1 style={titleStyle}>Team Efficiency Dashboard</h1>
          <p style={subtitleStyle}>
            Track how time is split between UI work, delivery, bug fixing, testing, retesting, and project management. This view highlights who is shipping new work and where rework is starting to grow.
          </p>
        </div>

        <div style={filterBarStyle}>
          {review?.canSelectEmployee && (
            <label style={fieldLabelStyle}>
              Employee
              <select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)} style={fieldInputStyle}>
                <option value="all">All employees</option>
                {review.employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>{employee.fullName}</option>
                ))}
              </select>
            </label>
          )}
          <label style={fieldLabelStyle}>
            Start
            <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} style={fieldInputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            End
            <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} style={fieldInputStyle} />
          </label>
          <button type="button" onClick={() => { setStartDate(addDaysToInput(today, -6)); setEndDate(today) }} style={resetButtonStyle}>
            Last 7 days
          </button>
        </div>
      </div>

      <div style={statsGridStyle}>
        <SummaryCard label="Feature Hours" value={formatHours(totals?.featureHours || 0)} icon={<Hammer size={18} />} tone="#2563eb" />
        <SummaryCard label="Bug Fix Hours" value={formatHours(totals?.bugFixHours || 0)} icon={<Bug size={18} />} tone="#ef4444" />
        <SummaryCard label="Bug Fix %" value={formatPercent(totals?.bugFixPercent || 0)} icon={<ShieldCheck size={18} />} tone={(totals?.bugFixPercent || 0) >= (review?.bugFixThreshold || 15) ? '#ef4444' : '#16a34a'} />
        <SummaryCard label="Testing Hours" value={formatHours(totals?.testingHours || 0)} icon={<BarChart3 size={18} />} tone="#16a34a" />
        <SummaryCard label="Retesting Hours" value={formatHours(totals?.retestingHours || 0)} icon={<Clock3 size={18} />} tone="#8b5cf6" />
        <SummaryCard label="Project Mgmt Hours" value={formatHours(totals?.projectManagementHours || 0)} icon={<Clock3 size={18} />} tone="#475569" />
        <SummaryCard label="Employees At Risk" value={String(totals?.employeesAtRisk || 0)} icon={<AlertTriangle size={18} />} tone={(totals?.employeesAtRisk || 0) > 0 ? '#f59e0b' : '#2563eb'} />
      </div>

      <div style={insightGridStyle}>
        <section style={panelStyle}>
          <div style={panelHeaderStyle}>
            <div>
              <h2 style={panelTitleStyle}>Work Trend</h2>
              <div style={panelSubtitleStyle}>
                {review ? `${format(new Date(review.startDate), 'dd MMM yyyy')} to ${format(new Date(review.endDate), 'dd MMM yyyy')}` : 'Selected range'}
              </div>
            </div>
            <div style={legendWrapStyle}>
              <LegendSwatch label="Feature" color="#2563eb" />
              <LegendSwatch label="Bug Fix" color="#ef4444" />
              <LegendSwatch label="Testing" color="#16a34a" />
              <LegendSwatch label="Retesting" color="#8b5cf6" />
              <LegendSwatch label="Project Management" color="#475569" />
            </div>
          </div>
          <TrendChart data={trend} />
        </section>

        <section style={panelStyle}>
          <div style={panelHeaderStyle}>
            <div>
              <h2 style={panelTitleStyle}>Focus Snapshot</h2>
              <div style={panelSubtitleStyle}>Most active employees in the selected range</div>
            </div>
            <div style={thresholdBadgeStyle(review?.bugFixThreshold || 15)}>Bug-fix threshold {review?.bugFixThreshold || 15}%</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {busiestEmployees.length === 0 ? (
              <div style={emptyBoxStyle}>No time logs in this range yet.</div>
            ) : (
              busiestEmployees.map((employee) => (
                <div key={employee.user.id} style={employeeMiniCardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <Avatar user={employee.user} size="sm" />
                    <div style={{ minWidth: 0 }}>
                      <div style={miniNameStyle}>{employee.user.fullName}</div>
                      <div style={miniRoleStyle}>{formatRoleLabel(employee.user.role)}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={miniValueStyle}>{formatHours(employee.totalHours)}</div>
                    <div style={{ ...miniRoleStyle, color: employee.bugFixPercent >= (review?.bugFixThreshold || 15) ? '#ef4444' : '#16a34a' }}>
                      Bug fix {formatPercent(employee.bugFixPercent)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      <section style={panelStyle}>
        <div style={panelHeaderStyle}>
          <div>
            <h2 style={panelTitleStyle}>Employee Comparison</h2>
            <div style={panelSubtitleStyle}>
              Structured time logs by role, task count, delivery mix, and rework pressure.
            </div>
          </div>
          <div style={legendGridStyle}>
            {chartWorkTypes.map((workType) => (
              <LegendSwatch key={workType} label={formatWorkType(workType)} color={workTypeColors[workType]} />
            ))}
          </div>
        </div>

        {isLoading ? (
          <div style={emptyBoxStyle}>Loading performance data...</div>
        ) : metrics.length === 0 ? (
          <div style={emptyBoxStyle}>No performance data available yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {metrics.map((employee) => (
              <EmployeeRow key={employee.user.id} employee={employee} bugFixThreshold={review?.bugFixThreshold || 15} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function SummaryCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string
  value: string
  icon: React.ReactNode
  tone: string
}) {
  return (
    <div style={summaryCardStyle}>
      <div style={{ ...summaryIconStyle, color: tone, background: `${tone}16` }}>{icon}</div>
      <div>
        <div style={summaryLabelStyle}>{label}</div>
        <div style={{ ...summaryValueStyle, color: tone }}>{value}</div>
      </div>
    </div>
  )
}

function LegendSwatch({ label, color }: { label: string; color: string }) {
  return (
    <span style={legendItemStyle}>
      <span style={{ ...legendDotStyle, background: color }} />
      {label}
    </span>
  )
}

function TrendChart({ data }: { data: PerformanceTrendPoint[] }) {
  if (data.length === 0) {
    return <div style={emptyBoxStyle}>No trend data to show yet.</div>
  }

  const width = 720
  const height = 240
  const padding = 24
  const maxY = Math.max(...data.map((point) => Math.max(
    point.featureHours,
    point.bugFixHours,
    point.testingHours,
    point.retestingHours,
    point.projectManagementHours,
    1
  )))
  const stepX = data.length > 1 ? (width - padding * 2) / (data.length - 1) : 0
  const toPoint = (index: number, value: number) => {
    const x = padding + stepX * index
    const y = height - padding - (value / maxY) * (height - padding * 2)
    return `${x},${y}`
  }

  const lineFor = (selector: (point: PerformanceTrendPoint) => number) =>
    data.map((point, index) => toPoint(index, selector(point))).join(' ')

  return (
    <div style={chartWrapStyle}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 250 }}>
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = height - padding - ratio * (height - padding * 2)
          return (
            <line
              key={ratio}
              x1={padding}
              x2={width - padding}
              y1={y}
              y2={y}
              stroke="#e2e8f0"
              strokeDasharray="4 4"
            />
          )
        })}
        <polyline fill="none" stroke="#2563eb" strokeWidth="3" points={lineFor((point) => point.featureHours)} />
        <polyline fill="none" stroke="#ef4444" strokeWidth="3" points={lineFor((point) => point.bugFixHours)} />
        <polyline fill="none" stroke="#16a34a" strokeWidth="3" points={lineFor((point) => point.testingHours)} />
        <polyline fill="none" stroke="#8b5cf6" strokeWidth="3" points={lineFor((point) => point.retestingHours)} />
        <polyline fill="none" stroke="#475569" strokeWidth="3" points={lineFor((point) => point.projectManagementHours)} />
      </svg>
      <div style={chartAxisStyle}>
        {data.map((point) => (
          <span key={point.date}>{format(new Date(point.date), data.length > 10 ? 'dd MMM' : 'EEE')}</span>
        ))}
      </div>
    </div>
  )
}

function EmployeeRow({
  employee,
  bugFixThreshold,
}: {
  employee: PerformanceEmployeeMetric
  bugFixThreshold: number
}) {
  const totalHours = Math.max(employee.totalHours, 0.01)
  const status = statusTone[employee.status]

  return (
    <div style={employeeCardStyle}>
      <div style={employeeTopStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <Avatar user={employee.user} size="md" />
          <div style={{ minWidth: 0 }}>
            <div style={employeeNameStyle}>{employee.user.fullName}</div>
            <div style={employeeRoleStyle}>{formatRoleLabel(employee.user.role)}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <span style={{ ...statusBadgeStyle, background: status.bg, color: status.color }}>{status.label}</span>
          <span style={inlineMetricStyle}>Tasks {employee.taskCount}</span>
          <span style={inlineMetricStyle}>Avg/day {formatHours(employee.averageHoursPerDay)}</span>
        </div>
      </div>

      <div style={stackTrackStyle}>
        {chartWorkTypes.map((workType) => {
          const hours = employee.workMix[workType]
          if (!hours) return null

          return (
            <div
              key={workType}
              title={`${formatWorkType(workType)} · ${formatHours(hours)}`}
              style={{
                height: '100%',
                width: `${(hours / totalHours) * 100}%`,
                background: workTypeColors[workType],
              }}
            />
          )
        })}
      </div>

      <div style={metricGridStyle}>
        <MetricBlock label="Total" value={formatHours(employee.totalHours)} />
        <MetricBlock label="Feature" value={formatHours(employee.featureHours)} tone="#2563eb" />
        <MetricBlock label="Bug Fix" value={formatHours(employee.bugFixHours)} tone={employee.bugFixPercent >= bugFixThreshold ? '#ef4444' : '#f97316'} />
        <MetricBlock label="Testing" value={formatHours(employee.testingHours)} tone="#16a34a" />
        <MetricBlock label="Retesting" value={formatHours(employee.retestingHours)} tone="#8b5cf6" />
        <MetricBlock label="Project Mgmt" value={formatHours(employee.projectManagementHours)} tone="#475569" />
        <MetricBlock label="Focus %" value={formatPercent(employee.focusPercent)} />
        <MetricBlock label="Bug Fix %" value={formatPercent(employee.bugFixPercent)} tone={employee.bugFixPercent >= bugFixThreshold ? '#ef4444' : '#16a34a'} />
      </div>
    </div>
  )
}

function MetricBlock({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div style={metricBlockStyle}>
      <div style={metricLabelStyle}>{label}</div>
      <div style={{ ...metricValueStyle, color: tone || '#111827' }}>{value}</div>
    </div>
  )
}

const pageStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '100%',
  boxSizing: 'border-box',
  padding: '24px',
  background:
    'radial-gradient(circle at top left, rgba(37,99,235,0.05), transparent 30%), linear-gradient(180deg, #f8fafc 0%, #f3f6fb 100%)',
}

const heroStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 24,
  marginBottom: 20,
}

const eyebrowStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#0f766e',
}

const titleStyle: React.CSSProperties = {
  margin: '6px 0 8px',
  fontSize: 30,
  lineHeight: 1.1,
  color: '#0f172a',
}

const subtitleStyle: React.CSSProperties = {
  margin: 0,
  maxWidth: 760,
  fontSize: 14,
  lineHeight: 1.6,
  color: '#64748b',
}

const filterBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 10,
  padding: 14,
  borderRadius: 16,
  border: '1px solid #dbe3ee',
  background: 'rgba(255,255,255,0.88)',
  boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)',
  backdropFilter: 'blur(8px)',
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 11,
  fontWeight: 800,
  color: '#64748b',
  textTransform: 'uppercase',
}

const fieldInputStyle: React.CSSProperties = {
  minWidth: 155,
  height: 38,
  borderRadius: 10,
  border: '1px solid #dbe3ee',
  padding: '0 11px',
  fontSize: 13,
  color: '#111827',
  background: '#ffffff',
}

const resetButtonStyle: React.CSSProperties = {
  height: 38,
  padding: '0 14px',
  borderRadius: 10,
  border: '1px solid #bfdbfe',
  background: '#eff6ff',
  color: '#1d4ed8',
  fontWeight: 700,
  cursor: 'pointer',
}

const statsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(150px, 1fr))',
  gap: 12,
  marginBottom: 18,
}

const summaryCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minHeight: 84,
  padding: '16px 18px',
  borderRadius: 18,
  border: '1px solid #e2e8f0',
  background: 'rgba(255,255,255,0.92)',
  boxShadow: '0 16px 32px rgba(15, 23, 42, 0.06)',
}

const summaryIconStyle: React.CSSProperties = {
  width: 42,
  height: 42,
  borderRadius: 12,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}

const summaryLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
}

const summaryValueStyle: React.CSSProperties = {
  marginTop: 5,
  fontSize: 24,
  fontWeight: 800,
}

const insightGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.65fr) minmax(300px, 0.95fr)',
  gap: 16,
  marginBottom: 18,
}

const panelStyle: React.CSSProperties = {
  borderRadius: 20,
  border: '1px solid #e2e8f0',
  background: 'rgba(255,255,255,0.95)',
  boxShadow: '0 18px 38px rgba(15, 23, 42, 0.06)',
  padding: 18,
}

const panelHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 14,
}

const panelTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 19,
  color: '#0f172a',
}

const panelSubtitleStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12.5,
  color: '#64748b',
}

const legendWrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  flexWrap: 'wrap',
}

const legendGridStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  maxWidth: '45%',
}

const legendItemStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 11,
  color: '#475569',
  fontWeight: 700,
}

const legendDotStyle: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: 999,
  flexShrink: 0,
}

const chartWrapStyle: React.CSSProperties = {
  width: '100%',
  overflowX: 'auto',
}

const chartAxisStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(50px, 1fr))',
  gap: 8,
  fontSize: 11,
  color: '#64748b',
  marginTop: -2,
}

const thresholdBadgeStyle = (threshold: number): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 30,
  borderRadius: 999,
  padding: '0 12px',
  background: '#fffbeb',
  color: '#92400e',
  fontSize: 12,
  fontWeight: 800,
})

const emptyBoxStyle: React.CSSProperties = {
  border: '1px dashed #cbd5e1',
  borderRadius: 14,
  padding: 22,
  fontSize: 13,
  color: '#94a3b8',
  textAlign: 'center',
}

const employeeMiniCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '12px 14px',
  borderRadius: 14,
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
}

const miniNameStyle: React.CSSProperties = {
  fontSize: 13.5,
  color: '#111827',
  fontWeight: 800,
}

const miniRoleStyle: React.CSSProperties = {
  marginTop: 2,
  fontSize: 11,
  color: '#64748b',
  textTransform: 'uppercase',
}

const miniValueStyle: React.CSSProperties = {
  fontSize: 16,
  color: '#0f172a',
  fontWeight: 800,
}

const employeeCardStyle: React.CSSProperties = {
  borderRadius: 18,
  border: '1px solid #e2e8f0',
  background: '#ffffff',
  padding: 16,
}

const employeeTopStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 14,
  marginBottom: 12,
}

const employeeNameStyle: React.CSSProperties = {
  fontSize: 15,
  color: '#0f172a',
  fontWeight: 800,
}

const employeeRoleStyle: React.CSSProperties = {
  marginTop: 2,
  fontSize: 11,
  color: '#64748b',
  textTransform: 'uppercase',
}

const statusBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 28,
  borderRadius: 999,
  padding: '0 11px',
  fontSize: 12,
  fontWeight: 800,
}

const inlineMetricStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#475569',
  fontWeight: 700,
}

const stackTrackStyle: React.CSSProperties = {
  height: 14,
  borderRadius: 999,
  background: '#e2e8f0',
  overflow: 'hidden',
  display: 'flex',
  marginBottom: 14,
}

const metricGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(8, minmax(96px, 1fr))',
  gap: 10,
}

const metricBlockStyle: React.CSSProperties = {
  minHeight: 60,
  borderRadius: 12,
  background: '#f8fafc',
  padding: '10px 12px',
  border: '1px solid #eef2f7',
}

const metricLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  fontWeight: 800,
  textTransform: 'uppercase',
}

const metricValueStyle: React.CSSProperties = {
  marginTop: 5,
  fontSize: 18,
  fontWeight: 800,
}
